import React, { useState } from 'react';
import { StyleSheet, Pressable } from 'react-native';
import { Iffy, Icon, DNABox, DNAText } from '@alucio/lux-ui';
import { useContentPreviewModal } from '../../State/ContentPreviewModalStateProvider';
import { useContent } from 'src/state/context/ContentProvider/ContentProvider';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import { getDocumentDisplaySummary } from 'src/components/DNA/Document/DNAAISummary';
import { tabConfigs } from 'src/components/ContentPreviewModalV2/SideBar';
import SidebarContent from 'src/components/ContentPreviewModalV2/SideBar/SideBarContent';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';

const styles = StyleSheet.create({
  summaryCard: {
    backgroundColor: colors['color-gray-80'],
    borderRadius: 6,
    padding: 12,
  },
  expandIcon: {
    padding: 0,
    height: 18,
    width: 18,
  },
  sidebarContentText: {
    maxWidth: 280,
  },
  documentIdText: {
    fontSize: 14,
  },
})

const ExpandIcon: React.FC<{ toggleLongDescription: boolean, onPress: () => void }> = (props) => {
  const { toggleLongDescription, onPress } = props;
  return (
    <Pressable onPress={onPress}>
      <DNABox alignY="center" spacing="xs">
        <Icon name={toggleLongDescription ? 'plus-square' : 'minus-square'} style={styles.expandIcon} />
        <DNAText bold>
          Long Description
        </DNAText>
      </DNABox>
    </Pressable>
  )
}

const SummaryCard: React.FC<{ shortSummary?: string }> = ({ shortSummary }) => {
  if (!shortSummary) return null;

  return (
    <DNABox appearance="col" spacing="xs" style={styles.summaryCard}>
      <DNAText bold>Summary</DNAText>
      <DNAText status="tertiary">{shortSummary}</DNAText>
    </DNABox>
  );
};

const Details: React.FC = () => {
  const { activeDocumentVersionORM } = useContentPreviewModal()
  const { contentPageData } = useContent();
  const enableDocumentSummaries = useFeatureFlags('enableDocumentSummaries');
  const [
    toggleLongDescription,
    setToggleLongDescription,
  ] = useState<boolean>(!!activeDocumentVersionORM?.model.longDescription);
  if (!activeDocumentVersionORM) return null
  const { customValues, integration } = activeDocumentVersionORM.meta;
  const shortSummary = getDocumentDisplaySummary(contentPageData, activeDocumentVersionORM.model.shortSummary);
  const sortedFieldLabels = Object.values(activeDocumentVersionORM.meta.customValues.configsMap).map(({ field }) =>
    field.fieldLabel).sort();
  const customFields = sortedFieldLabels?.map((fieldLabel, index: number) => {
    const field = Object.values(customValues.configsMap).find(({ field }) => field.fieldLabel === fieldLabel)

    return ( field && field.displayValues.length > 0
      ? <React.Fragment key={`modal_custom_fields_${index}`}>
        <DNABox appearance="col">
          <Iffy is={fieldLabel.toLowerCase() === 'long description'}>
            <ExpandIcon
              onPress={() => setToggleLongDescription(p => !p)}
              toggleLongDescription={toggleLongDescription}
            />
            <Iffy is={!toggleLongDescription}>
              <DNAText style={styles.sidebarContentText}>
                {field.displayValues[0]}
              </DNAText>
            </Iffy>
          </Iffy>
          <Iffy is={fieldLabel.toLowerCase() !== 'long description'}>
            <DNAText bold>{fieldLabel}</DNAText>
            {field.displayValues.map((value, index: number) => (
              <DNAText
                style={fieldLabel === 'Short Description' ? styles.sidebarContentText : null }
                key={`custom_field_${index}`}
              >
                {value}
              </DNAText>
            ))}
          </Iffy>
        </DNABox>
      </React.Fragment>
      : null
    )
  });

  return (
    <DNABox appearance="col">
      <DNABox spacing="md" appearance="col">
        {enableDocumentSummaries && <SummaryCard shortSummary={shortSummary} />}
        {customFields}
        {integration.source &&
          <>
            <DNAText bold>Content Source</DNAText>
            <DNAText style={styles.sidebarContentText}>{integration.source}</DNAText>
          </>
       }
        <DNABox appearance="col" spacing="between">
          <DNAText style={styles.documentIdText} status="subtle">
            Document Id:
          </DNAText>
          <DNAText style={styles.documentIdText} status="subtle">
            {activeDocumentVersionORM.model.documentId}
          </DNAText>
        </DNABox>
      </DNABox>
    </DNABox>
  )
}

const DetailsWrapper: React.FC = () => {
  return (
    <SidebarContent>
      <SidebarContent.Header
        sidebarHeader={tabConfigs.details.label}
      />
      <SidebarContent.Body>
        <Details/>
      </SidebarContent.Body>
    </SidebarContent>
  )
}

export default DetailsWrapper;
